import { TFunction } from 'i18next';
import { DraftOperationT } from 'routes/BudgetModule/config/types';
import { getOperationUrlBySoftwareId } from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/Operations/helpers/gridHelpers';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { translateBlock } from 'i18n';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { CURRENCY_EXCEL_CLASSNAMES } from 'routes/BudgetModule/config/excelExportHelpers';
import { currencyFormatter } from 'helpers/formatHelper';

export const getDraftOperationsColumns = ({
    primaryColor,
    referenceYear,
    t,
}: {
    primaryColor: string;
    referenceYear: number;
    t: TFunction;
}): ColDef[] => {
    const cellStyleBold = {
        fontWeight: 600,
        justifyContent: 'flex-start',
        color: primaryColor,
        maxWidth: '300px',
    };

    const numericCol: ColDef = {
        filter: 'agNumberColumnFilter',
        type: 'numericColumn',
        aggFunc: 'sum',
        sortable: true,
        floatingFilterComponent: 'numberFloatingFilter',
        useValueFormatterForExport: false,
        cellStyle: cellStyleBold,
        valueFormatter: (params: ValueFormatterParams<unknown, number>) =>
            currencyFormatter(params.value ?? 0, { maximumFractionDigits: 0 }),
        cellClass: CURRENCY_EXCEL_CLASSNAMES,
    };

    return [
        {
            field: 'name',
            headerName: translateBlock('secondaryBudgetAxisElement'),
            valueGetter: ({ data }: { data: DraftOperationT }) =>
                `${data.code} - ${data.name}`,
            sortable: true,
            sort: 'asc',
            filter: 'agTextColumnFilter',
            floatingFilterComponent: 'textFloatingFilter',
            enableRowGroup: true,
            cellStyle: {
                fontWeight: 510,
            },
            cellRenderer: 'agGroupCellRenderer',
            cellRendererParams: {
                getUrl: (op: DraftOperationT, location: LocationT) =>
                    getOperationUrlBySoftwareId(op.softwareId, location),
                getDisabled: (op: DraftOperationT) => !op.softwareId,
                innerRenderer: 'linkCellRenderer',
                style: {
                    fontWeight: '510',
                    textDecoration: 'underline',
                },
                onClick: (op: DraftOperationT) => {
                    trackEventBudget(
                        TRACKABLE_EVENTS.budget.budgetOverview.operations
                            .operationClicked,
                        {
                            exercice: referenceYear,
                            operation: op.name,
                        },
                    );
                },
            },
        },
        {
            field: 'serviceProposalTotal',
            headerName: t('createDraftOperation.serviceProposal', {
                year: referenceYear,
            }),
            ...numericCol,
        },
        {
            field: 'arbitrationTotal',
            headerName: t('createDraftOperation.simulatedArbitrage', {
                year: referenceYear,
            }),
            ...numericCol,
        },
        {
            field: 'supplementaryBudgetTotal',
            headerName: t('createDraftOperation.supplementaryBudget', {
                year: referenceYear,
            }),
            ...numericCol,
        },
        {
            field: 'actions',
            headerName: '',
            cellRenderer: 'draftOperationActionsCellRenderer',
            cellRendererParams: { exercice: referenceYear },
            suppressHeaderMenuButton: true,
            editable: false,
            suppressNavigable: true,
            maxWidth: 120,
            suppressColumnsToolPanel: true,
            resizable: false,
            suppressMovable: true,
        },
    ];
};
